import { Container } from '@/components/Container';
import { getPublished } from '@/data/loader';
import { useTranslation } from '@/hooks/useTranslation';
import googleThumbs from '../../../public/images/google/thumbs.json';

/* ═══════════════════════════════════════════
   ATRAÇÕES IMPERDÍVEIS
   Dados 100% do JSON atracoes.json
   ═══════════════════════════════════════════ */

export function AtracoesImperdiveisSection() {
  const { t } = useTranslation();
  const atracoes = getPublished('atracao');

  const principais = [
    atracoes.find((a: any) => a.handle === 'farol-das-conchas'),
    atracoes.find((a: any) => a.handle === 'gruta-das-encantadas'),
    atracoes.find((a: any) => a.handle === 'fortaleza-nossa-senhora-dos-prazeres'),
  ].filter(Boolean);

  return (
    <section id="atracoes" className="py-16 sm:py-20 bg-white">
      <Container>
        {/* Header */}
        <div className="text-center mb-12">
          <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
            {t('guide.atracoesImperdiveis.overline')}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl text-warm-dark mt-2">
            {t('guide.atracoesImperdiveis.title')}
          </h2>
          <p className="font-editorial text-lg text-warm-gray mt-2 max-w-2xl mx-auto">
            {t('guide.atracoesImperdiveis.subtitle')}
          </p>
        </div>

        {/* Cards das 3 principais */}
        <div className="space-y-8 mb-12">
          {principais.map((a: any, index: number) => (
            <div
              key={a.id}
              className={`rounded-2xl border border-sand-200 overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] ${
                index % 2 === 1 ? 'bg-sand-50' : 'bg-white'
              }`}
            >
              <div
                className={`grid md:grid-cols-2 gap-0 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
              >
                {/* Imagem */}
                <div
                  className={`relative h-64 md:h-auto min-h-[300px] ${index % 2 === 1 ? 'md:order-2' : ''}`}
                >
                  <img
                    src={
                      (googleThumbs as Record<string, string>)[a.handle] ||
                      a.image ||
                      '/images/praia-do-farol-hero.jpg'
                    }
                    alt={a.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="bg-coral text-white text-xs font-body font-bold px-3 py-1 rounded-full">
                      #{index + 1}
                    </span>
                  </div>
                </div>

                {/* Conteúdo */}
                <div
                  className={`p-6 lg:p-8 flex flex-col justify-center ${index % 2 === 1 ? 'md:order-1' : ''}`}
                >
                  <h3 className="font-display text-2xl text-warm-dark mb-2">
                    {a.title.toUpperCase()}
                  </h3>
                  <p className="font-body text-warm-gray leading-relaxed mb-4">{a.description}</p>

                  {/* Specs */}
                  <div className="flex flex-wrap gap-3 mb-5">
                    <span className="font-body text-xs bg-teal-50 text-teal-deep border border-teal-100 px-3 py-1 rounded-full">
                      ⏱️ {a.duration}
                    </span>
                    <span className="font-body text-xs bg-amber-50 text-amber-600 border border-amber-100 px-3 py-1 rounded-full">
                      📍 {a.difficulty}
                    </span>
                    <span className="font-body text-xs bg-sand-100 text-warm-dark border border-sand-200 px-3 py-1 rounded-full">
                      💰 {a.price}
                    </span>
                    {a.kidsFriendly && (
                      <span className="font-body text-xs bg-green-50 text-green-600 border border-green-100 px-3 py-1 rounded-full">
                        {t('guide.atracoesImperdiveis.kidsFriendly')}
                      </span>
                    )}
                  </div>

                  {/* Dados técnicos do JSON */}
                  {a.history && (
                    <div className="bg-sand-50 rounded-xl p-4 mb-4">
                      <h4 className="font-body text-sm font-semibold text-warm-dark mb-2">
                        {t('guide.atracoesImperdiveis.historyLabel')}
                      </h4>
                      <dl className="space-y-1.5">
                        {a.history.built && (
                          <div className="flex justify-between text-sm">
                            <dt className="font-body text-warm-gray">
                              {t('guide.atracoesImperdiveis.builtLabel')}
                            </dt>
                            <dd className="font-body text-warm-dark">{a.history.built}</dd>
                          </div>
                        )}
                        {a.history.orderBy && (
                          <div className="flex justify-between text-sm">
                            <dt className="font-body text-warm-gray">
                              {t('guide.atracoesImperdiveis.orderedByLabel')}
                            </dt>
                            <dd className="font-body text-warm-dark">{a.history.orderBy}</dd>
                          </div>
                        )}
                        {a.history.builder && (
                          <div className="flex justify-between text-sm">
                            <dt className="font-body text-warm-gray">
                              {t('guide.atracoesImperdiveis.builderLabel')}
                            </dt>
                            <dd className="font-body text-warm-dark">{a.history.builder}</dd>
                          </div>
                        )}
                        {a.history.engineer && (
                          <div className="flex justify-between text-sm">
                            <dt className="font-body text-warm-gray">
                              {t('guide.atracoesImperdiveis.engineerLabel')}
                            </dt>
                            <dd className="font-body text-warm-dark">{a.history.engineer}</dd>
                          </div>
                        )}
                        {a.history.wallThickness && (
                          <div className="flex justify-between text-sm">
                            <dt className="font-body text-warm-gray">
                              {t('guide.atracoesImperdiveis.wallsLabel')}
                            </dt>
                            <dd className="font-body text-warm-dark">{a.history.wallThickness}</dd>
                          </div>
                        )}
                        {a.history.status && (
                          <div className="flex justify-between text-sm">
                            <dt className="font-body text-warm-gray">
                              {t('guide.atracoesImperdiveis.statusLabel')}
                            </dt>
                            <dd className="font-body text-warm-dark">{a.history.status}</dd>
                          </div>
                        )}
                        {a.history.function && (
                          <div className="flex justify-between text-sm">
                            <dt className="font-body text-warm-gray">
                              {t('guide.atracoesImperdiveis.functionLabel')}
                            </dt>
                            <dd className="font-body text-warm-dark">{a.history.function}</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}

                  {/* Lenda */}
                  {a.legend && (
                    <div className="bg-teal-50/50 rounded-xl p-4 mb-4 border border-teal-100">
                      <h4 className="font-body text-sm font-semibold text-teal-deep mb-2">
                        {a.legend.title}
                      </h4>
                      <p className="font-body text-sm text-warm-gray italic leading-relaxed">
                        {a.legend.text}
                      </p>
                    </div>
                  )}

                  {/* Highlights */}
                  {a.highlights?.length > 0 && (
                    <div className="space-y-1.5">
                      {a.highlights.slice(0, 3).map((h: string) => (
                        <div key={h} className="flex items-start gap-2">
                          <svg
                            className="w-4 h-4 text-teal-deep mt-0.5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="font-body text-sm text-warm-gray">{h}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Dica de horário do Farol das Conchas */}
                  {a.handle === 'farol-das-conchas' && (
                    <div className="mt-2 flex items-center gap-1.5">
                      <span role="img" aria-label="Câmera" className="text-sm">
                        📸
                      </span>
                      <p className="font-body text-xs text-teal-deep font-medium">
                        Melhor foto: fim da tarde (luz dourada)
                      </p>
                    </div>
                  )}

                  {/* Aviso Gruta das Encantadas */}
                  {a.handle === 'gruta-das-encantadas' && (
                    <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
                      <span role="img" aria-label="Atenção" className="text-sm flex-shrink-0">
                        ⚠️
                      </span>
                      <p className="font-body text-xs text-warm-gray">
                        Visite na <strong className="text-warm-dark">maré baixa</strong>. Na maré
                        alta a entrada fica submersa. Consulte a tábua de marés antes de ir.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Outras atrações em grid */}
        <div>
          <h3 className="font-display text-xl text-warm-dark mb-4 text-center">
            {t('guide.atracoesImperdiveis.moreAttractionsTitle')}
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {atracoes
              .filter(
                (a: any) =>
                  ![
                    'farol-das-conchas',
                    'gruta-das-encantadas',
                    'fortaleza-nossa-senhora-dos-prazeres',
                  ].includes(a.handle)
              )
              .map((a: any) => (
                <div
                  key={a.id}
                  className="bg-white rounded-xl border border-sand-200 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                >
                  <h4 className="font-display text-lg text-warm-dark mb-1">
                    {a.title.toUpperCase()}
                  </h4>
                  <p className="font-body text-sm text-warm-gray mb-3 line-clamp-2">{a.subtitle}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="font-body text-xs bg-teal-50 text-teal-deep px-2 py-0.5 rounded-full">
                      {a.duration}
                    </span>
                    <span className="font-body text-xs bg-sand-100 text-warm-dark px-2 py-0.5 rounded-full">
                      {a.price}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </Container>
    </section>
  );
}
